import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaMicrophone } from 'react-icons/fa';

const AudioPlayer = ({ audioUrl, fileName }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => { setCurrentTime(audio.currentTime); setProgress((audio.currentTime / audio.duration) * 100); };
    const onMeta = () => setDuration(audio.duration);
    const onEnd = () => { setIsPlaying(false); setProgress(0); setCurrentTime(0); };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('loadedmetadata', onMeta); audio.removeEventListener('ended', onEnd); };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = pct * audioRef.current.duration;
    }
  };

  const fmtTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-r from-[#4A90E2]/5 to-[#A855F7]/5 border border-[#4A90E2]/20 rounded-xl p-4" data-testid="audio-player">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      <div className="flex items-center space-x-3">
        <button
          onClick={togglePlay}
          className="w-11 h-11 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity flex-shrink-0"
          data-testid="audio-play-btn"
        >
          {isPlaying ? <FaPause size={12} /> : <FaPlay size={12} className="ml-0.5" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <FaMicrophone className="text-[#4A90E2] text-xs" />
            <span className="text-xs font-medium text-gray-700 truncate">{fileName || 'Audio'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-[10px] text-gray-400 w-8">{fmtTime(currentTime)}</span>
            <div
              className="flex-1 h-1.5 bg-gray-200 rounded-full cursor-pointer group"
              onClick={seek}
            >
              <div
                className="h-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] rounded-full transition-all relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-[#4A90E2] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className="text-[10px] text-gray-400 w-8 text-right">{fmtTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
